import { useState, useMemo } from 'react'
import {
  BarChart3, ThumbsUp, MessageCircle, Share2, FileText, Loader2, Image as ImageIcon, Facebook,
  Trash2, Pencil, Send, X, ChevronDown, ChevronUp, Shield, Eye, Code, User,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { useV1Accounts } from '@/api/hooks/useInstagram'
import {
  usePageEngagement, useEditFBPost, useDeleteFBPost,
  useFBPostComments, useAddFBComment, useDeleteFBComment,
} from '@/api/hooks/useEngagement'
import { useQueryClient } from '@tanstack/react-query'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

import { isStagingMode } from '@/lib/staging'
// ── REVIEW MODE: auto-activates on staging.blissf.com ──
const REVIEW_MODE = isStagingMode()

const fmtNum = new Intl.NumberFormat('en-US')

function formatDate(ts: string, language: string) {
  const locale = language === 'en' ? 'en-US' : 'pt-BR'
  return new Date(ts).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function timeAgo(ts: string, language: string): string {
  const diff = Date.now() - new Date(ts).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return language === 'en' ? 'just now' : 'agora'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  return `${weeks}w`
}

// ── Debug overlay for API calls ──
function ApiDebugBanner({ method, endpoint, fields, permission, description }: {
  method: string
  endpoint: string
  fields?: string
  permission: string
  description: string
}) {
  if (!REVIEW_MODE) return null
  const permColor = permission === 'pages_read_user_content'
    ? 'border-emerald-500/40 bg-emerald-500/5'
    : permission === 'pages_read_engagement'
      ? 'border-blue-500/40 bg-blue-500/5'
      : 'border-orange-500/40 bg-orange-500/5'
  const badgeColor = permission === 'pages_read_user_content'
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : permission === 'pages_read_engagement'
      ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
      : 'bg-orange-500/15 text-orange-400 border-orange-500/30'

  return (
    <div className={`rounded-lg border px-3 py-2 ${permColor}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Code className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs font-mono font-semibold text-foreground">
          {method} {endpoint}
        </span>
        {fields && (
          <span className="text-[10px] font-mono text-muted-foreground">
            ?fields={fields}
          </span>
        )}
        <Badge variant="outline" className={`text-[10px] font-mono ml-auto ${badgeColor}`}>
          {permission}
        </Badge>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{description}</p>
    </div>
  )
}

// ── Highlight for user-content fields ──
function UserContentHighlight({ label, value }: { label: string; value: string }) {
  if (!REVIEW_MODE) return <span className="text-xs font-semibold">{value}</span>
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs font-semibold">{value}</span>
      <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
        {label}
      </span>
    </span>
  )
}

// ── Comments section for a single post ──
function PostComments({ pageId, postId }: { pageId: string; postId: string }) {
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { data, isLoading } = useFBPostComments(pageId, postId)
  const addComment = useAddFBComment()
  const deleteComment = useDeleteFBComment()
  const [newComment, setNewComment] = useState('')

  const comments = data?.data || []

  const handleAdd = () => {
    const msg = newComment.trim()
    if (!msg) return
    addComment.mutate({ pageId, postId, message: msg }, {
      onSuccess: () => {
        toast.success('Comment added!')
        setNewComment('')
        queryClient.invalidateQueries({ queryKey: ['fb-comments', pageId, postId] })
        queryClient.invalidateQueries({ queryKey: ['page-engagement', pageId] })
      },
      onError: (err: any) => toast.error(err?.message || 'Error adding comment'),
    })
  }

  const handleDelete = (commentId: string) => {
    if (!confirm('Delete this comment?')) return
    deleteComment.mutate({ pageId, commentId }, {
      onSuccess: () => {
        toast.success('Comment deleted!')
        queryClient.invalidateQueries({ queryKey: ['fb-comments', pageId, postId] })
        queryClient.invalidateQueries({ queryKey: ['page-engagement', pageId] })
      },
      onError: (err: any) => toast.error(err?.message || 'Error deleting comment'),
    })
  }

  return (
    <div className="border-t border-border px-4 py-3 space-y-3">
      {/* API debug for reading comments */}
      <ApiDebugBanner
        method="GET"
        endpoint={`/${postId}/comments`}
        fields="id,message,created_time,from"
        permission="pages_read_user_content"
        description="Reads user-generated comments on this post. The 'from' field (commenter name and user ID) requires pages_read_user_content permission."
      />

      {isLoading ? (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">No comments yet</p>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {comments.map(comment => (
            <div key={comment.id} className="group">
              <div className="flex items-start gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
                  {(comment.from?.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="rounded-xl bg-muted/50 px-3 py-1.5">
                    {/* Commenter name — this field requires pages_read_user_content */}
                    <UserContentHighlight label="from.name" value={comment.from?.name || 'Unknown'} />
                    <p className="text-xs">{comment.message}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 px-1 flex-wrap">
                    <span className="text-[9px] text-muted-foreground">{formatDate(comment.created_time, 'en')}</span>
                    {REVIEW_MODE && (
                      <>
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-muted text-muted-foreground">
                          comment.id: {comment.id}
                        </span>
                        {comment.from?.id && (
                          <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                            from.id: {comment.from.id}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                  onClick={() => handleDelete(comment.id)}
                  disabled={deleteComment.isPending}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add comment as Page */}
      <div className="flex gap-2">
        <Input
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Write a comment as Page..."
          className="h-8 text-xs"
          onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
          disabled={addComment.isPending}
        />
        <Button
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={handleAdd}
          disabled={addComment.isPending || !newComment.trim()}
        >
          {addComment.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        </Button>
      </div>
      {REVIEW_MODE && (
        <ApiDebugBanner
          method="POST"
          endpoint={`/${postId}/comments`}
          fields="message"
          permission="pages_manage_posts"
          description="Posts a comment as the Page on this post."
        />
      )}
    </div>
  )
}

export function Engagement() {
  const { t, language } = useLanguage()
  const queryClient = useQueryClient()
  const { data: accounts, isLoading: loadingAccounts } = useV1Accounts()
  const [selectedPage, setSelectedPage] = useState<string>('')
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set())
  const [editingPost, setEditingPost] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  const editFBPost = useEditFBPost()
  const deleteFBPost = useDeleteFBPost()

  const pageAccounts = (accounts || []).filter(a => a.pageId)

  const pageId = selectedPage || pageAccounts[0]?.pageId || ''
  const currentAccount = pageAccounts.find(a => a.pageId === pageId)
  const pageName = currentAccount?.name || currentAccount?.username || ''

  const { data: engagementData, isLoading: loadingPosts } = usePageEngagement(pageId || undefined)

  const posts = engagementData?.data || []

  const stats = useMemo(() => {
    let totalLikes = 0, totalComments = 0, totalShares = 0
    for (const post of posts) {
      totalLikes += post.likes?.summary?.total_count || 0
      totalComments += post.comments?.summary?.total_count || 0
      totalShares += post.shares?.count || 0
    }
    return { totalPosts: posts.length, totalLikes, totalComments, totalShares }
  }, [posts])

  const toggleComments = (postId: string) => {
    setExpandedComments(prev => {
      const next = new Set(prev)
      if (next.has(postId)) next.delete(postId)
      else next.add(postId)
      return next
    })
  }

  const handleEditPost = (postId: string) => {
    if (!editText.trim()) return
    editFBPost.mutate({ pageId, postId, message: editText.trim() }, {
      onSuccess: () => {
        toast.success('Post updated!')
        setEditingPost(null)
        setEditText('')
        queryClient.invalidateQueries({ queryKey: ['page-engagement', pageId] })
      },
      onError: (err: any) => toast.error(err?.message || 'Error editing post'),
    })
  }

  const handleDeletePost = (postId: string) => {
    if (!confirm('Delete this Facebook post? This action cannot be undone.')) return
    deleteFBPost.mutate({ pageId, postId }, {
      onSuccess: () => {
        toast.success('Post deleted!')
        queryClient.invalidateQueries({ queryKey: ['page-engagement', pageId] })
      },
      onError: (err: any) => toast.error(err?.message || 'Error deleting post'),
    })
  }

  if (loadingAccounts) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    )
  }

  if (pageAccounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-muted-foreground">
        <BarChart3 className="h-12 w-12" />
        <p className="text-lg font-medium">No page connected</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Engagement</h1>
            <p className="text-sm text-muted-foreground">Gerencie os posts das suas Páginas do Facebook</p>
          </div>
        </div>
        {pageAccounts.length > 1 && (
          <Select value={pageId} onValueChange={setSelectedPage}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Select page" />
            </SelectTrigger>
            <SelectContent>
              {pageAccounts.map(a => (
                <SelectItem key={a.pageId} value={a.pageId}>
                  {a.name || a.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ── REVIEW MODE: Permission explanation panel ── */}
      {REVIEW_MODE && pageId && (
        <Card className="border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-500" />
            <h2 className="text-sm font-bold text-emerald-500">
              Meta App Review — Permission Usage Demo
            </h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This dashboard demonstrates how <strong>Blissf</strong> uses Facebook Graph API permissions
            to help business owners monitor and manage engagement on their Facebook Pages.
            Each API call below is annotated with the specific permission it requires.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Eye className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-[11px] font-bold text-emerald-500">pages_read_user_content</span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-snug">
                Read comments posted by <strong>Page visitors</strong>. Returns the commenter's name (from.name) and User ID (from.id).
                This is essential for our CRM — without it we cannot identify who commented.
              </p>
            </div>
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1">
                <BarChart3 className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-[11px] font-bold text-blue-500">pages_read_engagement</span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-snug">
                Read the Page's own posts, photos, and engagement metrics (likes count, comments count, shares count).
              </p>
            </div>
            <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Pencil className="h-3.5 w-3.5 text-orange-500" />
                <span className="text-[11px] font-bold text-orange-500">pages_manage_posts</span>
              </div>
              <p className="text-[10px] text-muted-foreground leading-snug">
                Create, edit and delete Page posts. Also used to post comments as the Page and delete user comments.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Connected Page info */}
      {REVIEW_MODE && pageId && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-[11px] font-mono gap-1.5 px-2.5 py-1">
            <Facebook className="h-3 w-3 text-blue-500" />
            Connected Page: {pageName}
          </Badge>
          <Badge variant="outline" className="text-[11px] font-mono gap-1.5 px-2.5 py-1">
            Page ID: {pageId}
          </Badge>
        </div>
      )}

      {/* API call debug: fetching posts */}
      {REVIEW_MODE && pageId && (
        <ApiDebugBanner
          method="GET"
          endpoint={`/${pageId}/posts`}
          fields="id,message,created_time,full_picture,likes.summary(true),comments.summary(true),shares"
          permission="pages_read_engagement"
          description="Fetches all posts published by the Page with engagement metrics (likes, comments, shares counts). This data populates the stats cards and the feed below."
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xl font-bold">{fmtNum.format(stats.totalPosts)}</p>
              <p className="text-xs text-muted-foreground">Posts</p>
            </div>
          </div>
        </Card>
        <Card className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-500/10">
              <ThumbsUp className="h-5 w-5 text-pink-500" />
            </div>
            <div>
              <p className="text-xl font-bold">{fmtNum.format(stats.totalLikes)}</p>
              <p className="text-xs text-muted-foreground">Curtidas</p>
            </div>
          </div>
        </Card>
        <Card className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <MessageCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-xl font-bold">{fmtNum.format(stats.totalComments)}</p>
              <p className="text-xs text-muted-foreground">Comentários</p>
            </div>
          </div>
        </Card>
        <Card className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
              <Share2 className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xl font-bold">{fmtNum.format(stats.totalShares)}</p>
              <p className="text-xs text-muted-foreground">Compartilhamentos</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Feed */}
      {loadingPosts ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No posts found</p>
        </div>
      ) : (
        <div className="mx-auto max-w-xl space-y-5">
          {posts.map(post => {
            const likes = post.likes?.summary?.total_count || 0
            const comments = post.comments?.summary?.total_count || 0
            const shares = post.shares?.count || 0
            const isEditing = editingPost === post.id
            const commentsOpen = expandedComments.has(post.id)

            return (
              <Card key={post.id} className="overflow-hidden rounded-xl border border-border bg-card">
                {/* Post header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/15">
                    <Facebook className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{pageName}</p>
                    {REVIEW_MODE && (
                      <p className="text-[10px] text-muted-foreground font-mono">Post ID: {post.id}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-blue-500"
                      title="Edit post (pages_manage_posts)"
                      onClick={() => {
                        if (isEditing) {
                          setEditingPost(null)
                          setEditText('')
                        } else {
                          setEditingPost(post.id)
                          setEditText(post.message || '')
                        }
                      }}
                    >
                      {isEditing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      title="Delete post (pages_manage_posts)"
                      onClick={() => handleDeletePost(post.id)}
                      disabled={deleteFBPost.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <span className="text-xs text-muted-foreground">{timeAgo(post.created_time, language)}</span>
                </div>

                {/* Edit mode */}
                {isEditing && (
                  <div className="px-4 pb-3 space-y-2">
                    <Textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      className="text-sm min-h-[80px]"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="gap-1.5"
                        onClick={() => handleEditPost(post.id)}
                        disabled={editFBPost.isPending || !editText.trim()}
                      >
                        {editFBPost.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditingPost(null); setEditText('') }}>
                        Cancel
                      </Button>
                    </div>
                    {REVIEW_MODE && (
                      <ApiDebugBanner
                        method="POST"
                        endpoint={`/${post.id}`}
                        fields="message"
                        permission="pages_manage_posts"
                        description="Updates the message text of this published post."
                      />
                    )}
                  </div>
                )}

                {/* Post image */}
                {post.full_picture ? (
                  <div className="relative w-full bg-black/5">
                    <img src={post.full_picture} alt="" className="w-full max-h-[500px] object-contain" />
                  </div>
                ) : (
                  <div className="flex h-48 w-full items-center justify-center bg-muted/30">
                    <ImageIcon className="h-12 w-12 text-muted-foreground/20" />
                  </div>
                )}

                {/* Engagement bar */}
                <div className="flex items-center gap-5 px-4 py-2.5">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <ThumbsUp className="h-5 w-5" />
                  </div>
                  <button
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-blue-500 transition-colors"
                    onClick={() => toggleComments(post.id)}
                  >
                    <MessageCircle className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Share2 className="h-5 w-5" />
                  </div>
                </div>

                {/* Metrics */}
                <div className="px-4 pb-1">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-semibold">{fmtNum.format(likes)} Likes</span>
                    <span className="text-muted-foreground">&middot;</span>
                    <span className="text-muted-foreground">{fmtNum.format(comments)} Comments</span>
                    <span className="text-muted-foreground">&middot;</span>
                    <span className="text-muted-foreground">{fmtNum.format(shares)} Shares</span>
                  </div>
                </div>

                {/* Post text */}
                {post.message && !isEditing && (
                  <div className="px-4 pb-2">
                    <p className="text-sm">
                      <span className="font-semibold mr-1.5">{pageName}</span>
                      {post.message.length > 200 ? post.message.slice(0, 200) + '...' : post.message}
                    </p>
                  </div>
                )}

                {/* View comments toggle */}
                <div className="px-4 pb-2">
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => toggleComments(post.id)}
                  >
                    {commentsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {commentsOpen
                      ? 'Hide comments'
                      : comments > 0
                        ? `View ${comments} comment${comments !== 1 ? 's' : ''}`
                        : 'Comment'
                    }
                    {REVIEW_MODE && !commentsOpen && comments > 0 && (
                      <span className="text-[9px] font-mono text-emerald-500 ml-1">
                        (pages_read_user_content)
                      </span>
                    )}
                  </button>
                </div>

                {/* Comments section */}
                {commentsOpen && <PostComments pageId={pageId} postId={post.id} />}

                {/* Date + Post ID */}
                <div className="px-4 pb-3 flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground uppercase">{formatDate(post.created_time, language)}</span>
                  {REVIEW_MODE && (
                    <span className="text-[9px] font-mono text-muted-foreground/50">Post ID: {post.id}</span>
                  )}
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* ── REVIEW MODE: Bottom summary ── */}
      {REVIEW_MODE && posts.length > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2 max-w-xl mx-auto">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-emerald-500" />
            <h3 className="text-xs font-bold text-emerald-500">
              Summary: pages_read_user_content usage
            </h3>
          </div>
          <ul className="text-[11px] text-muted-foreground space-y-1 list-disc list-inside">
            <li>
              <strong>Read user comments</strong> — GET /{'{post-id}'}/comments?fields=id,message,<strong>from</strong>,created_time
            </li>
            <li>
              <strong>from.name</strong> — Displays the commenter's name in the UI (shown with green badge on each comment)
            </li>
            <li>
              <strong>from.id</strong> — Identifies the commenter for CRM tracking and automated responses
            </li>
            <li>
              <strong>Comment monitoring</strong> — Background polling every 30s detects new comments for real-time CRM workflows
            </li>
          </ul>
          <p className="text-[10px] text-muted-foreground italic">
            Without pages_read_user_content, the app cannot retrieve comment content or identify commenters,
            making comment monitoring and customer engagement features impossible.
          </p>
        </Card>
      )}
    </div>
  )
}
